import { useQuery } from '@tanstack/react-query'
import type { V2GetStartupOutreachOutcomesResponse } from '@workspace/api-backend'
import { v2GetStartupOutreachOutcomesOptions } from '@workspace/api-backend/react-query'
import { useMemo } from 'react'
import type { RosterMatch } from '../components/outreach-active-view'

/** One reply as the outcomes endpoint returns it. */
type OutreachReply = Extract<
  V2GetStartupOutreachOutcomesResponse,
  { status: 'ok' }
>['data']['replies']['investors'][number]

/** Contacts who replied, split by the campaign that reached them. */
export interface OutreachReplies {
  investors: RosterMatch[]
  portfolio: RosterMatch[]
  total: number
}

function replyToRoster(reply: OutreachReply, kind: 'investor' | 'warm_intro'): RosterMatch {
  return {
    investorId: reply.investorId,
    investorName: reply.investorName,
    contactName: reply.contactName,
    contactTitle: reply.contactTitle,
    contactScope: null,
    group: 'engaged',
    stage: reply.callBooked ? 'Call booked' : 'Replied',
    charges: {
      views: reply.views,
      returnVisits: reply.returnVisits,
      calls: reply.callBooked ? 1 : 0,
    },
    lastReply: reply.summary ?? undefined,
    redirect: reply.redirect ?? undefined,
    messageId: reply.messageId,
    unread: reply.seenAt == null,
    lastEventAt: reply.repliedAt,
    linkedinUrl: reply.conversationUrl ?? undefined,
    // What Archive acts on — the lead for an investor, the message itself for a
    // warm intro. The two archive endpoints differ; the id each needs does not
    // travel any further than the button.
    leadId: reply.archiveId ?? undefined,
    kind,
  }
}

/**
 * Single source of truth for "who replied", across both campaigns. Feeds the
 * Outcomes page and the sidebar badge, so the two can't disagree.
 *
 * Read from the outcomes endpoint, which resolves each reply from the message
 * row and the investor it was written against. Deliberately NOT from the
 * campaign queue: that board is assembled from the current match set, and a
 * rematch replaces matches wholesale — so a fund we already wrote to can stop
 * being a match while its conversation is still live, and the replies would
 * vanish from the page while the badge went on counting them.
 *
 * Replies are historical: a contact who answered still counts even if the
 * channel has since been switched off, which is why neither side is gated on
 * its enabled flag the way the approval counts are.
 */
export function useOutreachReplies(
  startupId: string,
  options?: { enabled?: boolean }
): OutreachReplies {
  const query = useQuery({
    ...v2GetStartupOutreachOutcomesOptions({ path: { id: startupId } }),
    enabled: options?.enabled ?? true,
  })
  const replies = query.data?.status === 'ok' ? query.data.data.replies : null

  return useMemo(() => {
    const investors = (replies?.investors ?? []).map((r) => replyToRoster(r, 'investor'))
    const portfolio = (replies?.portfolio ?? []).map((r) => replyToRoster(r, 'warm_intro'))
    return { investors, portfolio, total: investors.length + portfolio.length }
  }, [replies])
}
